import { useSnackbar } from '@/features/snackbar';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMemo } from 'react';

type MutationWithSnackbarOptions = {
  includeSuccessSnackbar?: boolean;
  successMessage?: string;
  errorMessage?: string;
};

export const useMutationWithSnackbar = <
  TData = unknown,
  TVariables = unknown,
  TError = unknown,
>(
  mutation: UseMutationResult<TData, TError, TVariables>,
  options: MutationWithSnackbarOptions = {}
) => {
  const { addSnackbar } = useSnackbar();
  const {
    includeSuccessSnackbar = false,
    successMessage = 'Successfully saved',
    errorMessage = 'Operation failed',
  } = options;

  return useMemo(
    () => ({
      ...mutation,
      mutate: (variables: TVariables): Promise<TData> => {
        return new Promise((resolve, reject) => {
          mutation.mutate(variables, {
            onSuccess: async (result) => {
              if (includeSuccessSnackbar) {
                addSnackbar({
                  type: 'positive',
                  lifeTime: 2000,
                  message: successMessage,
                });
              }
              resolve(result);
            },
            onError: async (error) => {
              addSnackbar({
                type: 'negative',
                lifeTime: 2000,
                message: errorMessage,
              });
              reject(error);
            },
          });
        });
      },
    }),
    [
      mutation,
      includeSuccessSnackbar,
      successMessage,
      errorMessage,
      addSnackbar,
    ]
  );
};
